import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../../data";
import { useAction, useRueckfrage } from "../../components/ui";
import { Fehler, Laden } from "./parts";

/**
 * Rechtstexte (0182, Anwaltsvorgabe 19): Änderungen von AGB, Nutzungsbedingungen, Datenschutzhinweisen und AVV sowie neue
 * Unterauftragsverarbeiter ankündigen. Die Ankündigung erzeugt einen Hinweis im Portal für alle Admins der Kunden und je
 * Admin eine Mail über den Postausgang (Empfängerprotokoll unten). Die neue Fassung gilt erst ab dem Gültig-ab-Datum; erst dann
 * fragt das Portal die erneute Zustimmung ab. Neue Unterauftragsverarbeiter frühestens 30 Tage nach der Ankündigung
 * (Widerspruchsfrist, AVV). Nur deutsch wie die übrige Konsole.
 */

interface Fassung { kind: string; version: string; valid_from: string; valid_until: string | null; url: string; archive_url: string | null; summary_de: string | null }
interface Ankuendigung {
  id: string; notice_type: "change" | "subprocessor"; kind: string; new_version: string | null; valid_from: string; objection_until: string | null;
  material: boolean; summary_de: string; summary_en: string; url: string | null; created_at: string; created_by_email: string | null;
  recipients: number; sent: number; failed: number; open: number; activated_at: string | null; cancelled_at: string | null;
}
interface Empfaenger { email: string; language: string; orgs: string | null; status: string; sent_at: string | null; last_error: string | null }

const DOKUMENTE: Record<string, { name: string; seite: string; archiv: string }> = {
  agency_terms: { name: "AGB", seite: "https://creatoroffice.de/agb", archiv: "agb" },
  user_terms: { name: "Nutzungsbedingungen", seite: "https://creatoroffice.de/nutzung", archiv: "nutzung" },
  privacy: { name: "Datenschutzhinweise", seite: "https://creatoroffice.de/datenschutz", archiv: "datenschutz" },
  dpa: { name: "Auftragsverarbeitungsvertrag (AVV)", seite: "https://creatoroffice.de/avv", archiv: "avv" },
};
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;
const dmy = (iso: string | null) => (iso ? iso.slice(0, 10).split("-").reverse().join(".") : "–");
const heute = () => new Date().toLocaleDateString("sv-SE");
const plusTage = (n: number) => new Date(Date.now() + n * 86400000).toLocaleDateString("sv-SE");

export function Rechtstexte() {
  const qc = useQueryClient();
  const fassungen = useQuery({ queryKey: ["legal_document_versions", "betrieb"], queryFn: () => api.select<Fassung>("legal_document_versions", { order: [["kind", "asc"], ["version", "desc"]] }) });
  const aktuell = useQuery({ queryKey: ["legal_versions_current", "betrieb"], queryFn: async () => parse<Record<string, string>>(await api.read("legal_versions_current")) });
  const liste = useQuery({ queryKey: ["platform_legal_changes"], queryFn: async () => parse<Ankuendigung[]>(await api.read("platform_legal_changes")) });
  const [offen, setOffen] = useState<string | null>(null);
  const reload = () => {
    void qc.invalidateQueries({ queryKey: ["platform_legal_changes"] });
    void qc.invalidateQueries({ queryKey: ["legal_document_versions"] });
    void qc.invalidateQueries({ queryKey: ["legal_versions_current"] });
  };
  if (liste.error) return <Fehler error={liste.error} retry={() => void liste.refetch()} />;
  return (
    <>
      <title>Rechtstexte · Plattform · CreatorOffice</title>
      <h1>Rechtstexte</h1>
      <p>Änderungen der Rechtstexte und neue Unterauftragsverarbeiter vorher ankündigen. Die Admins aller Kunden bekommen einen Hinweis im Portal und eine Mail; die neue Fassung gilt erst ab dem Gültig-ab-Datum, dann fragt das Portal die erneute Zustimmung ab. Der Zustimmungshinweis sperrt niemanden.</p>

      <section className="panel stack" aria-labelledby="fassungen-titel">
        <h2 id="fassungen-titel">Fassungen</h2>
        {fassungen.isPending || aktuell.isPending ? <Laden /> : fassungen.error ? <Fehler error={fassungen.error} /> : (
          <div className="tablewrap">
            <table>
              <caption className="sr-only">Fassungen der Rechtstexte</caption>
              <thead><tr><th scope="col">Dokument</th><th scope="col">Fassung</th><th scope="col">Gültig</th><th scope="col">Stand</th><th scope="col">Adresse</th></tr></thead>
              <tbody>
                {(fassungen.data ?? []).map((f) => {
                  const cur = aktuell.data?.[f.kind];
                  const stand = f.version === cur ? "aktuell" : cur && f.version > cur ? "angekündigt" : "früher";
                  return (
                    <tr key={`${f.kind}-${f.version}`}>
                      <td>{DOKUMENTE[f.kind]?.name ?? f.kind}</td>
                      <td>{dmy(f.version)}{f.summary_de && <small>{f.summary_de}</small>}</td>
                      <td>ab {dmy(f.valid_from)}{f.valid_until ? ` bis ${dmy(f.valid_until)}` : ""}</td>
                      <td>{stand}</td>
                      <td><a href={f.url} target="_blank" rel="noreferrer">{f.url.replace("https://", "")}</a>{f.archive_url && f.archive_url !== f.url && <small>Archiv: <a href={f.archive_url} target="_blank" rel="noreferrer">{f.archive_url.replace("https://", "")}</a></small>}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <AnkuendigungFormular aktuell={aktuell.data ?? {}} onDone={reload} />

      <section className="panel stack" aria-labelledby="ankuendigungen-titel">
        <h2 id="ankuendigungen-titel">Ankündigungen</h2>
        {liste.isPending ? <Laden /> : (liste.data ?? []).length === 0 ? <p>Noch keine Ankündigung.</p> : (
          <div className="tablewrap">
            <table>
              <caption className="sr-only">Angekündigte Änderungen mit Versandstand</caption>
              <thead><tr><th scope="col">Änderung</th><th scope="col">Gültig ab</th><th scope="col">Versand</th><th scope="col">Stand</th><th scope="col"><span className="sr-only">Aktion</span></th></tr></thead>
              <tbody>
                {(liste.data ?? []).map((a) => (
                  <ZeileAnkuendigung key={a.id} a={a} offen={offen === a.id} onToggle={() => setOffen(offen === a.id ? null : a.id)} onDone={reload} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

function ZeileAnkuendigung({ a, offen, onToggle, onDone }: { a: Ankuendigung; offen: boolean; onToggle: () => void; onDone: () => void }) {
  const { busy, error, attempt } = useAction();
  const rueckfrage = useRueckfrage();
  const empfaenger = useQuery({ queryKey: ["platform_legal_change_recipients", a.id], enabled: offen,
    queryFn: () => api.read<Empfaenger[]>("platform_legal_change_recipients", { p_notice: a.id }) });
  const stand = a.cancelled_at ? `zurückgezogen am ${dmy(a.cancelled_at)}` : a.activated_at ? `wirksam seit ${dmy(a.activated_at)}` : a.valid_from <= heute() ? "wirksam (Tageslauf ausstehend)" : "angekündigt";
  return (
    <>
      <tr>
        <td>
          <strong>{a.notice_type === "subprocessor" ? "Neuer Unterauftragsverarbeiter" : `${DOKUMENTE[a.kind]?.name ?? a.kind}, Fassung vom ${dmy(a.new_version)}`}</strong>
          {a.material && <small>erheblich nachteilig: Hinweis auf Sonderkündigungsrecht</small>}
          <small>{a.summary_de}</small>
          {a.objection_until && <small>Widerspruchsfrist bis {dmy(a.objection_until)}</small>}
          <small>angelegt {dmy(a.created_at)}{a.created_by_email ? ` von ${a.created_by_email}` : ""}</small>
        </td>
        <td>{dmy(a.valid_from)}</td>
        <td>{a.recipients} Admins · {a.sent} versendet · {a.open} offen{a.failed ? ` · ${a.failed} gescheitert` : ""}</td>
        <td>{stand}</td>
        <td>
          <div className="row">
            <button type="button" onClick={onToggle} aria-expanded={offen}>{offen ? "Empfänger ausblenden" : "Empfänger"}</button>
            {!a.cancelled_at && !a.activated_at && a.valid_from > heute() && (
              <button type="button" className="danger" disabled={!!busy} onClick={async () => {
                if (!(await rueckfrage.ask({ title: "Ankündigung zurückziehen?", text: "Noch nicht verschickte Mails gehen nicht mehr raus, die angekündigte Fassung verschwindet aus dem Archiv. Schon verschickte Mails bleiben verschickt; bitte die Kunden dann gesondert informieren.", confirmLabel: "Zurückziehen", danger: true }))) return;
                void attempt("zurueck", () => api.rpc("platform_legal_change_cancel", { p_notice: a.id }), "Ankündigung zurückgezogen.").then((ok) => ok && onDone());
              }}>Zurückziehen</button>
            )}
          </div>
          {rueckfrage.element}
          {error && <p className="tk-error" role="alert">{error}</p>}
        </td>
      </tr>
      {offen && (
        <tr>
          <td colSpan={5}>
            {empfaenger.isPending ? <Laden /> : empfaenger.error ? <Fehler error={empfaenger.error} /> : (
              <ul>
                {(empfaenger.data ?? []).map((e) => (
                  <li key={e.email}>{e.email} ({e.language}) · {e.orgs ?? "–"} · {e.status}{e.sent_at ? ` am ${dmy(e.sent_at)}` : ""}{e.last_error ? ` · ${e.last_error}` : ""}</li>
                ))}
              </ul>
            )}
          </td>
        </tr>
      )}
    </>
  );
}

function AnkuendigungFormular({ aktuell, onDone }: { aktuell: Record<string, string>; onDone: () => void }) {
  const { busy, error, attempt } = useAction();
  const rueckfrage = useRueckfrage();
  const [art, setArt] = useState<"change" | "subprocessor">("change");
  const [kind, setKind] = useState("agency_terms");
  const [gueltig, setGueltig] = useState("");
  const [version, setVersion] = useState("");
  const [url, setUrl] = useState("");
  const [archiv, setArchiv] = useState("");
  const [de, setDe] = useState("");
  const [en, setEn] = useState("");
  const [nachteilig, setNachteilig] = useState(false);
  // 0425: Neukunden stimmen der neuen Fassung sofort zu (Bestandskunden ab dem Gültig-ab-Datum); Hinweis-Mail abschaltbar
  const [neukunden, setNeukunden] = useState(true);
  const [mail, setMail] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);
  const cur = aktuell[kind] ?? null;
  const fruehestens = art === "subprocessor" ? plusTage(30) : plusTage(1);
  const neueFassung = art === "change" ? (version || gueltig) : version;
  const vorlauf = /^\d{4}-\d{2}-\d{2}$/.test(gueltig) ? Math.round((Date.parse(gueltig) - Date.parse(heute())) / 86400000) : null;
  const archivVorschlag = cur ? `https://creatoroffice.de/archiv/${DOKUMENTE[kind]?.archiv ?? kind}-${cur}.html` : "";

  function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(gueltig)) return setFehler("Bitte das Gültig-ab-Datum angeben.");
    if (gueltig < fruehestens) return setFehler(art === "subprocessor" ? `Neue Unterauftragsverarbeiter frühestens ab ${dmy(fruehestens)} (30 Tage Widerspruchsfrist).` : "Das Gültig-ab-Datum muss in der Zukunft liegen.");
    if (art === "change" && !/^\d{4}-\d{2}-\d{2}$/.test(neueFassung)) return setFehler("Bitte die neue Fassung als Datum angeben.");
    if (!de.trim() || !en.trim()) return setFehler("Bitte die Zusammenfassung auf Deutsch und Englisch eintragen.");
    setFehler(null);
    void (async () => {
      const ok = await rueckfrage.ask({
        title: "Änderung ankündigen?",
        text: `${art === "subprocessor" ? "Neuer Unterauftragsverarbeiter" : `${DOKUMENTE[kind]?.name}, Fassung vom ${dmy(neueFassung)}`}, gültig ab ${dmy(gueltig)}. Alle Admins der Kunden bekommen jetzt einen Hinweis im Portal${mail ? " und eine Mail" : " (ohne Mail)"}.${art === "change" && neukunden ? " Neukunden stimmen der neuen Fassung ab sofort zu." : ""} Das lässt sich nicht zurückholen.`,
        confirmLabel: mail ? "Ankündigen und Mails einreihen" : "Ankündigen ohne Mail",
      });
      if (!ok) return;
      const done = await attempt("ankuendigen", () => api.rpc("platform_legal_change_announce", {
        p_type: art, p_kind: kind, p_version: neueFassung || null, p_valid_from: gueltig, p_summary_de: de.trim(), p_summary_en: en.trim(),
        p_material: art === "change" && nachteilig, p_url: url.trim() || null, p_archive_url: archiv.trim() || null,
        p_new_customers_now: art === "change" && neukunden, p_send_mail: mail,
      }), mail ? "Angekündigt. Die Mails stehen im Postausgang." : "Angekündigt, ohne Mail.");
      if (done) { setGueltig(""); setVersion(""); setUrl(""); setArchiv(""); setDe(""); setEn(""); setNachteilig(false); onDone(); }
    })();
  }

  return (
    <section className="panel stack" aria-labelledby="ankuendigen-titel">
      {rueckfrage.element}
      <h2 id="ankuendigen-titel">Änderung ankündigen</h2>
      <form className="stack" onSubmit={submit} noValidate>
        <fieldset className="row">
          <legend>Art</legend>
          <label className="row tk-check"><input type="radio" name="art" checked={art === "change"} onChange={() => setArt("change")} /><span>Neue Fassung eines Rechtstexts</span></label>
          <label className="row tk-check"><input type="radio" name="art" checked={art === "subprocessor"} onChange={() => { setArt("subprocessor"); setKind("dpa"); }} /><span>Neuer Unterauftragsverarbeiter</span></label>
        </fieldset>
        <div className="grid">
          <label>Dokument<select value={kind} onChange={(e) => setKind(e.target.value)}>
            {Object.entries(DOKUMENTE).map(([k, d]) => <option key={k} value={k}>{d.name}</option>)}
          </select><small>Geltende Fassung: {cur ? dmy(cur) : "–"}</small></label>
          <label>Gültig ab<input type="date" min={fruehestens} value={gueltig} onChange={(e) => setGueltig(e.target.value)} />
            <small>{vorlauf !== null ? `${vorlauf} Tage Vorlauf` : art === "subprocessor" ? `frühestens ${dmy(fruehestens)}` : "in der Zukunft"}</small></label>
          <label>Neue Fassung (Datum){art === "subprocessor" ? " (freiwillig)" : ""}<input type="date" value={version} onChange={(e) => setVersion(e.target.value)} />
            <small>{art === "change" ? "leer: das Gültig-ab-Datum" : "nur wenn sich der Text ändert"}</small></label>
        </div>
        <div className="grid">
          <label>Adresse der neuen Fassung (freiwillig)<input type="url" value={url} onChange={(e) => setUrl(e.target.value)} placeholder={DOKUMENTE[kind]?.seite} /></label>
          <label>Archivadresse der bisherigen Fassung (freiwillig)<input type="url" value={archiv} onChange={(e) => setArchiv(e.target.value)} placeholder={archivVorschlag} />
            <small>Die Website legt die Archivdatei unter /archiv/ ab; ab dem Gültig-ab-Datum zeigt das Archiv der App dorthin.</small></label>
        </div>
        <label>Zusammenfassung (Deutsch)<textarea rows={3} maxLength={2000} value={de} onChange={(e) => setDe(e.target.value)}
          placeholder={art === "subprocessor" ? "Wer, wofür, Speicherort, Rechtsgrundlage der Übermittlung" : "Was ändert sich für die Kunden?"} /></label>
        <label>Zusammenfassung (Englisch)<textarea rows={3} maxLength={2000} value={en} onChange={(e) => setEn(e.target.value)} /></label>
        {art === "change" && (
          <label className="row tk-check"><input type="checkbox" checked={nachteilig} onChange={(e) => setNachteilig(e.target.checked)} />
            <span>Erheblich nachteilige Änderung: Hinweis auf das Sonderkündigungsrecht zum Gültig-ab-Datum in Mail und Portal</span></label>
        )}
        {art === "change" && (
          <label className="row tk-check"><input type="checkbox" checked={neukunden} onChange={(e) => setNeukunden(e.target.checked)} />
            <span>Neukunden stimmen der neuen Fassung sofort zu (Registrierung, neue Admins); Bestandskunden erst ab dem Gültig-ab-Datum</span></label>
        )}
        <label className="row tk-check"><input type="checkbox" checked={mail} onChange={(e) => setMail(e.target.checked)} />
          <span>Hinweis-Mail an die Admins aller Kunden (aus, wenn dieselbe Änderung schon mit einer anderen Ankündigung gemailt wird)</span></label>
        {art === "subprocessor" && <p className="notice">Die Kunden können 30 Tage ab heute (bis {dmy(plusTage(30))}) aus wichtigem Grund per Mail an die Datenschutzadresse widersprechen (Produkt-Standard privacy_email, leer: Supportadresse).</p>}
        {(fehler || error) && <p className="tk-error" role="alert">{fehler ?? error}</p>}
        <div className="row"><button type="submit" className="primary" disabled={!!busy} aria-busy={busy === "ankuendigen"}>{busy === "ankuendigen" ? "Wird angekündigt …" : "Ankündigen"}</button></div>
      </form>
    </section>
  );
}
